import React, { useEffect, useMemo, useRef, useState } from 'react';
import AppNavbar from '../Navbar/AppNavbar';
import Footer from '../Navbar/Footer';
import "../../assets/css/subscription.scss";
import { useDispatch, useSelector } from 'react-redux';
import { loadSubscriptionData } from '../redux/subscriptionServices/sb_action';
import { Box, Chip, Skeleton } from '@mui/material';
import "react-datepicker/dist/react-datepicker.css";
import { capitalizeFirstLetter, getInitials } from '../CommonFile/Function_utils';
import moment from 'moment/moment';
import { Circles } from 'react-loader-spinner';
import { useLocation } from 'react-router-dom';
import FilterBar from '../CommonFile/FilterBar';
import PageBreadcome from '../CommonFile/PageBreadcome';
import DataGridTable from '../CommonFile/DataGridTable';

function SubscriptionPage() {

    const dispatch = useDispatch();
    const location = useLocation();

    const { subscriptonData, subscriptonSummary, totalPages } = useSelector(state => state.sbndata);
    const { totalDataCount } = useSelector(state => state.data);
    const isSummary = Object.keys(subscriptonSummary).length > 0 ? true : false;

    const [loadData, setLoadData] = useState(true)
    const [tableHeight, setTableHeight] = useState(0);
    const [activeStatus, setActiveStatus] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [filterValues, setFilterValues] = useState({
        page: 1,
        page_size: 20,
        search_text: "",
        sbn_type: "",
        plan_type: "",
        active_status: "Active",
        from_date: null,
        to_date: null,
    })

    const divRef = useRef(null);
    const div1Ref = useRef(null);
    const div2Ref = useRef(null);
    const div3Ref = useRef(null);
    const [height1, setHeight1] = useState(0);
    const [height2, setHeight2] = useState(0);
    const [height3, setHeight3] = useState(0);

    // region set table height
    useEffect(() => {
        if (div1Ref.current) {
            setHeight1(div1Ref.current.offsetHeight);
        }
        if (div2Ref.current) {
            setHeight2(div2Ref.current.offsetHeight);
        }
        if (div3Ref.current) {
            setHeight3(div3Ref.current.offsetHeight);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!divRef.current) return;

        const parentHeight = divRef.current.offsetHeight + 370;



        const pixelAdjustment = height1 + height2 + height3 + 65;

        const tablePxHeight = parentHeight - pixelAdjustment;

        setTableHeight(tablePxHeight);
    }, [height1, height2, height3]);
    // endregion

    useEffect(() => {
        const today = new Date();

        // First day of the current month
        const firstDayOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
        );

        let filters = {}

        if (location.state) {
            if (location.state?.get_status) {
                filters = {
                    ...filterValues,
                    from_date: null,
                    to_date: null,
                }
                setFilterValues(filters);
            } else if (location.state?.sbn_type) {
                filters = {
                    ...filterValues,
                    sbn_type: location.state?.sbn_type,
                    from_date: null,
                    to_date: null,
                }
                setFilterValues(filters);
            } else if (location.state?.active_status) {
                filters = {
                    ...filterValues,
                    active_status: location.state?.active_status,
                    from_date: null,
                    to_date: null,
                }
                setFilterValues(filters);
            }
        } else {
            filters = {
                ...filterValues,
                from_date: firstDayOfMonth,
                to_date: today,
            }
            setFilterValues(filters);

        }

        setLoadData(true)
        pageLoadFunction(filters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // region Define DataGrid columns
    const columns = useMemo(() => {
        // Base columns shared by BOTH Active and Inactive views
        const baseColumns = [
            {
                field: 'name', headerName: 'Name', flex: 1, minWidth: 200,
                renderCell: (params) => {
                    if (params.row.isSkeleton) {
                        return <Skeleton variant="text" width="80%" />
                    }

                    return (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',         // Creates a clean space between avatar and text
                            fontWeight: '600'
                        }}>
                            <span className="profile-avatar-table">
                                {params.value && getInitials(params.value.trim())}
                            </span>
                            <span>
                                {params.value ?? ""}
                            </span>
                        </div>
                    );
                }
            },
            {
                field: 'email', headerName: 'Email', flex: 2, minWidth: 200,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="80%" />
                    ) : (
                        params.value
                    ),
            },
            {
                field: 'current_plan', headerName: 'Plan', minWidth: 150,
                renderCell: (params) => {
                    if (params.row.isSkeleton) {
                        return <Skeleton variant="text" width="80%" />
                    }

                    const plan = (params.value || 'FREE').toUpperCase();

                    // Define your plan color map
                    let colorSettings = { bg: '#c3d8fa', text: '#3b82f6' }; // Default / Free

                    if (plan === 'BASIC') {
                        colorSettings = { bg: '#f5e6cd', text: '#f59e0b' }; // Soft Blue
                    } else if (plan === 'PROFESSIONAL') {
                        colorSettings = { bg: '#c8f8e8', text: '#10b981' }; // Soft Green
                    } else if (plan === 'ENTERPRISE') {
                        colorSettings = { bg: '#fccfcf', text: '#ef4444' }; // Soft Purple
                    }

                    // If using MUI DataGrid:
                    return (
                        <Chip
                            label={capitalizeFirstLetter(params.value)}
                            size="small"
                            sx={{
                                backgroundColor: colorSettings.bg,
                                color: colorSettings.text,
                                fontWeight: 600,
                                // Target the actual text container inside the Chip
                                '& .MuiChip-label': {
                                    fontSize: '0.75rem',
                                }
                            }}
                        />
                    );
                }
            },
            {
                field: 'created_at', headerName: 'Subscribed On', flex: 1, minWidth: 150,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="80%" />
                    ) : (
                        params.value
                    ),
            },
        ];

        if (activeStatus) {
            return [
                ...baseColumns,
                {
                    field: 'deleted_at', headerName: 'Deleted On', flex: 1, minWidth: 180,
                    renderCell: (params) => {

                        if (params.row.isSkeleton) {
                            return <Skeleton variant="text" width="80%" />
                        }

                        return (
                            <span >
                                {params.value && moment(params.value.trim()).format("DD MMM, YYYY - HH:MM A")}
                            </span>
                        );
                    }
                },
            ];
        }

        if (!activeStatus) {
            return [
                ...baseColumns,
                {
                    field: 'available_credits', headerName: 'Credits Available', flex: 1, minWidth: 150,
                    renderCell: (params) => {
                        // Parse the numerical value correctly, fallback to 0 if null/undefined
                        if (params.row.isSkeleton) {
                            return <Skeleton variant="text" width="80%" />
                        }

                        const credits = Number(params.value) || 0;

                        // Define your fallback default color settings
                        let colorSettings = { text: '#3b82f6' }; // Default Gray for higher values or edge cases

                        if (credits < 10) {
                            colorSettings = { text: '#ef4444' }; // Soft Red (Low credits)
                        } else if (credits < 100) {
                            colorSettings = { text: '#f59e0b' }; // Soft Amber/Orange
                        } else if (credits < 500) {
                            colorSettings = { text: '#4c4e53' }; // Soft Blue
                        } else if (credits > 1000) {
                            colorSettings = { text: '#10b981' }; // Soft Green (High credits)
                        }

                        return (
                            <span style={{ color: colorSettings.text, fontWeight: '600' }}>
                                {params.value ?? 0}
                            </span>
                        );
                    }
                },
                {
                    field: 'start_date', headerName: 'Subscription Renewed', flex: 1, minWidth: 180,
                    renderCell: (params) =>
                        params.row.isSkeleton ? (
                            <Skeleton variant="text" width="80%" />
                        ) : (
                            params.value
                        )
                },
                {
                    field: 'end_date', headerName: 'Subscription Till', flex: 1, minWidth: 180,
                    renderCell: (params) =>
                        params.row.isSkeleton ? (
                            <Skeleton variant="text" width="80%" />
                        ) : (
                            params.value
                        ),
                },
                {
                    field: 'plan_type', headerName: 'Plan Type', flex: 1, minWidth: 130,
                    renderCell: (params) => {

                        if (params.row.isSkeleton) {
                            return <Skeleton variant="text" width="80%" />
                        }

                        const plan_type = (params.value || 'FREE').toUpperCase();

                        // Define your plan color map
                        let colorSettings = { bg: '#c3d8fa', text: '#3b82f6' }; // Default / Free

                        if (plan_type === 'MONTHLY') {
                            colorSettings = { bg: '#f5e6cd', text: '#f59e0b' }; // Soft Blue
                        }

                        // If using MUI DataGrid:
                        return (
                            <Chip
                                label={capitalizeFirstLetter(params.value)}
                                size="small"
                                sx={{
                                    backgroundColor: colorSettings.bg,
                                    color: colorSettings.text,
                                    fontWeight: 600,
                                    // Target the actual text container inside the Chip
                                    '& .MuiChip-label': {
                                        fontSize: '0.75rem',
                                    }
                                }}
                            />
                        );
                    }
                },
            ];
        }
    }, [filterValues, activeStatus]);

    const pageLoadFunction = (filterValues) => {
        if (filterValues.active_status === "Inactive") {
            setActiveStatus(true);
        } else {
            setActiveStatus(false);
        }
        const params = {
            page: filterValues.page,
            page_size: filterValues.page_size,
            search_text: filterValues.search_text,
            sbn_type: filterValues.sbn_type,
            plan_type: filterValues.plan_type,
            active_status: filterValues.active_status,
            from_date: filterValues.from_date ? moment(filterValues.from_date).format("YYYY-MM-DD") : null,
            to_date: filterValues.to_date ? moment(filterValues.to_date).format("YYYY-MM-DD") : null,
        }

        dispatch(loadSubscriptionData(params)).then(
            (resp) => {
                setLoadData(false)
                setLoadingMore(false)
            }
        )
            .catch((err) => {
                setLoadData(false)
                setLoadingMore(false)
            });
    }

    const searchFunction = (filterValues) => {
        const filter = {
            ...filterValues,
            page: 1
        }
        setLoadingMore(true)
        setFilterValues(filter)
        pageLoadFunction(filter)
    }

    // #region fucntion for clear filters
    const clearFunction = () => {
        const today = new Date();

        const firstDayOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
        );

        const filter = {
            page: 1,
            page_size: 10,
            search_text: "",
            sbn_type: "",
            plan_type: "",
            active_status: "Active",
            from_date: firstDayOfMonth,
            to_date: today,
        }
        setLoadingMore(true)
        setFilterValues(filter)
        pageLoadFunction(filter);
    }
    // #endregion

    // #region handle scroll
    useEffect(() => {
        const container = document.querySelector('.MuiDataGrid-virtualScroller');
        if (!container) return;

        let lastScrollTop = 0;

        const handleScroll = () => {

            if (!container || loadingMore) return;

            const currentScrollTop = container.scrollTop;

            if (currentScrollTop <= lastScrollTop) return;

            lastScrollTop = currentScrollTop;

            const distanceFromBottom = container.scrollHeight - currentScrollTop - container.clientHeight;

            if (distanceFromBottom === 0 && subscriptonData.length < totalDataCount && !loadingMore) {

                setLoadingMore(true);
                setFilterValues((prev) => {
                    if (prev.page >= totalPages) return prev;

                    return {
                        ...prev,
                        page: prev.page + 1,
                    };
                });
                const filter = {
                    ...filterValues,
                    page: filterValues.page + 1
                }
                pageLoadFunction(filter);
            }
        };

        container.addEventListener('scroll', handleScroll);

        // Cleanup function
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingMore, subscriptonData, totalDataCount, setFilterValues]);
    // #endregion


    const handleFilterValues = (e) => {
        const { name, value } = e.target;

        setFilterValues((prev) => ({
            ...prev,
            [name]: value,
            page: 1,
        }));
    }

    const handleDateChange = (name, date) => {
        setFilterValues((prev) => {
            const updated = {
                ...prev,
                [name]: date,
                page: 1,
            };

            // If From Date is cleared, clear To Date as well
            if (name === "from_date" && !date) {
                updated.to_date = null;
            }

            // If From Date changes and To Date is before it, clear To Date
            if (
                name === "from_date" &&
                date &&
                updated.to_date &&
                updated.to_date < date
            ) {
                updated.to_date = null;
            }

            return updated;
        });
    };

    // #region handling empty rows
    const dataRows = loadData
        ? Array.from({ length: 12 }, (_, index) => ({
            id: `skeleton-${index}`, // unique key
            isSkeleton: true,
        }))
        : subscriptonData.map((row, index) => ({
            ...row,
            id: row.id ?? row.id ?? `row-${index}`, // unique key for real rows
        }));
    // #endregion


    console.log(tableHeight, "tableHeight")

    return (
        <div className="page-layout" style={{ position: "relative" }}>
            <AppNavbar />
            <div className="page-content">
                <div className='subcription-main-page'>
                    <div className="subscription-page" ref={divRef}>

                        <div ref={div1Ref}>
                            <PageBreadcome heading="Subscription Management"
                                sub_heading="Manage customer subscriptions, plans." />
                        </div>

                        <div className="stats-row" ref={div2Ref}>

                            <div className="stat-card">
                                {/* Free Subscription: Gray/Slate color */}
                                <i className='bx bx-check-circle' style={{ color: '#1257c7' }}></i>
                                <h3>{isSummary ? ((subscriptonSummary.free_count + subscriptonSummary.basic_count +
                                    subscriptonSummary.professional_count + subscriptonSummary.enterprise_count) ?? 0) :
                                    <Skeleton width="40px" height="30px" />}</h3>
                                <span>Total</span>
                            </div>

                            <div className="stat-card">
                                {/* Free Subscription: Gray/Slate color */}
                                <i className='bx bx-check-circle' style={{ color: '#3b82f6' }}></i>
                                <h3>{isSummary ? (subscriptonSummary.free_count ?? 0) :
                                    <Skeleton width="40px" height="30px" />}</h3>
                                <span>Free Subscription</span>
                            </div>

                            <div className="stat-card">
                                {/* Basic: Soft Blue color */}
                                <i className='bx bx-time' style={{ color: '#f59e0b' }}></i>
                                <h3>{isSummary ? (subscriptonSummary.basic_count ?? 0) :
                                    <Skeleton width="40px" height="30px" />}</h3>
                                <span>Basic</span>
                            </div>

                            <div className="stat-card">
                                {/* Professional: Vibrant Green color */}
                                <i className='bx bx-rupee' style={{ color: '#10b981' }}></i>
                                <h3>{isSummary ? (subscriptonSummary.professional_count ?? 0) :
                                    <Skeleton width="40px" height="30px" />}</h3>
                                <span>Professional</span>
                            </div>

                            <div className="stat-card">
                                {/* Enterprise: Royal Purple color */}
                                <i className='bx bx-wallet' style={{ color: '#8b5cf6' }}></i>
                                <h3>{isSummary ? (subscriptonSummary.enterprise_count ?? 0) :
                                    <Skeleton width="40px" height="30px" />}</h3>
                                <span>Enterprise</span>
                            </div>

                            <div className="stat-card">
                                {/* Deleted Account: Warning Red color */}
                                <i className='bx bx-trash' style={{ color: '#ef4444' }}></i> {/* Changed icon to 'bx-trash' to better fit a deleted status */}
                                <h3>{isSummary && (subscriptonSummary.deleted_count ?? 0)}</h3>
                                <span>Deleted Account</span>
                            </div>

                        </div>

                        <div ref={div3Ref}>
                            <FilterBar filterValues={filterValues}
                                handleFilterValues={handleFilterValues}
                                handleDateChange={handleDateChange} displayPage="Subscription"
                                searchFunction={() => searchFunction(filterValues)} clearFunction={clearFunction} />
                        </div>

                        <div className="subscription-content">

                            {/* Table comes here */}
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <DataGridTable rowData={dataRows} className="dg-subscription"
                                    columns={columns} height={`${tableHeight}px !important`}
                                    page="subscription"
                                />
                                {loadingMore && <div className='loader-css'>
                                    <div className="d-flex justify-content-center align-items-center py-4">
                                        <Circles
                                            height="30"
                                            width="30"
                                            color="#007bff"
                                            ariaLabel="circles-loading"
                                            visible={true}
                                        />
                                    </div>
                                </div>}
                            </Box>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    )
}

export default SubscriptionPage
